<template>
  <swipeable-horizontal-scroll-list
    :key="productTimeSlotsKey"
    class="product-time-slots"
    :list="timeSlots"
    id-key="slot"
    :slides-per-group="3"
    :list-container-class="['gap-1 items-center', props.timeSlotsContainerClass]"
    :arrow-btn-class="arrowBtnClass"
    :arrow-left-btn-class="arrowLeftBtnClass"
    :arrow-right-btn-class="arrowRightBtnClass"
    :arrow-icon-class="arrowIconClass"
    :arrow-icon-left-class="arrowIconLeftClass"
    :arrow-icon-right-class="arrowIconRightClass"
  >
    <template #default="{ item: slotInfo, index }">
      <div class="relative" :class="{ 'pl-1': index === 0 && slotInfo.label }">
        <component
          :is="slotComponent"
          :to="slotInfo.link"
          class="product-time-slot hover:bg-orange-50 active:bg-orange-60 cursor-pointer flex flex-none flex-col justify-center items-center bg-no-repeat !text-gray-10"
          :class="[
            slotInfo.type === 'time' && props.selectedSlot === slotInfo.slot ? 'bg-orange-50' : 'bg-red-50',
            twMerge('size-14', props.timeSlotClass),
          ]"
          :aria-label="getSlotAriaLabel(slotInfo)"
          @click="onSlotClick(slotInfo)"
        >
          <template v-if="slotInfo.type === 'time'">
            <span class="relative top-3px font-medium text-xs">{{ slotInfo.slot?.split?.(' ')?.at?.(1) }}</span>
            <span class="relative -top-3px font-medium font-bold flex justify-center items-end">
              -{{ slotInfo.discount }}
              <span class="font-medium text-xs">%</span>
            </span>
          </template>
          <span v-else class="text-sm">{{ $t('card.date.more') }}</span>
        </component>
        <div
          v-if="slotInfo.label"
          class="absolute top-0 px-1 py-1px bg-orange-10 text-orange-60 text-8px leading-11px pointer-events-none rounded-5"
          :class="index === 0 ? 'left-0' : '-left-1'"
        >
          {{ slotInfo.label }}
        </div>
      </div>
    </template>
  </swipeable-horizontal-scroll-list>
</template>

<script lang="ts" setup>
import { twMerge } from 'tailwind-merge'

import type { FbranchDiscountSlot } from '@/types/api'
import type { SlotInfo } from '@/types/product'
import LocaleLink from '@/components/global/locale-link.vue'
import SwipeableHorizontalScrollList from '~/components/common/swipeable-horizontal-scroll-list.vue'

interface Props {
  slots: FbranchDiscountSlot[]
  maxSlotsCount?: number
  timeSlotsContainerClass?: string
  timeSlotClass?: string
  selectedSlot?: string
  link?: string
  isMoreShown?: boolean
  isDateLabelShown?: boolean
  arrowBtnClass?: string
  arrowIconClass?: string
  arrowLeftBtnClass?: string
  arrowIconLeftClass?: string
  arrowRightBtnClass?: string
  arrowIconRightClass?: string
  productName?: string
}

interface Emits {
  (event: 'slot-click', slotInfo: SlotInfo): void
}

const { t } = useI18n()
const props = withDefaults(defineProps<Props>(), { isMoreShown: false, isDateLabelShown: false })
const { slots, maxSlotsCount, isMoreShown, link, isDateLabelShown } = toRefs(props)

const emits = defineEmits<Emits>()

const productTimeSlotsKey = computed(() => slots.value.map(slotInfo => `${slotInfo.slot}_${slotInfo.discount}`).join())
const slotComponent = computed(() => (props.link ? LocaleLink : 'div'))

const { timeSlots } = useProductTimeSlot({
  slots,
  maxSlotsCount,
  isMoreShown,
  link,
  isDateLabelShown,
})

function onSlotClick(slotInfo: SlotInfo) {
  emits('slot-click', slotInfo)
}

function getSlotAriaLabel(slotInfo: SlotInfo) {
  if (slotInfo.type === 'time') {
    return `${props.productName}: ${slotInfo.slot} -${slotInfo.discount}%`
  }
  return `${props.productName}: ${t('card.date.more')}`
}
</script>

<style lang="scss" scoped>
.product-time-slot {
  mask-image: url('/images/product-time-bg.svg');
  mask-size: contain;
}
</style>
